/**
 * Route Configuration Utilities
 * Helper functions for route management
 */
import { AUTH_ROUTES, PROTECTED_ROUTES } from './constants';

/**
 * Generate breadcrumbs from route path
 * @param {string} path - Route path
 * @returns {Array} Breadcrumb array
 */
export const generateBreadcrumbs = (path) => {
  const segments = path.split('/').filter(Boolean);
  return segments.map((segment, index) => ({
    label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
    path: '/' + segments.slice(0, index + 1).join('/'),
  }));
};

/**
 * Check if route requires authentication
 * @param {Object} route - Route configuration
 * @returns {boolean}
 */
export const requiresAuth = (route) => {
  return route.meta?.requiresAuth || false;
};

/**
 * Check if route requires guest (unauthenticated)
 * @param {Object} route - Route configuration
 * @returns {boolean}
 */
export const requiresGuest = (route) => {
  return route.meta?.requiresGuest || false;
};

/**
 * Check if user has required role for route
 * @param {Object} route - Route configuration
 * @param {string} userRole - User's role
 * @returns {boolean}
 */
export const hasRequiredRole = (route, userRole) => {
  const requiredRoles = route.meta?.roles;
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }
  const normalizedUserRole = userRole?.toString().toUpperCase();
  return requiredRoles.some((role) => role.toString().toUpperCase() === normalizedUserRole);
};

/**
 * Resolve default landing route from backend user role
 * @param {string} role - User role from backend (ADMIN | INTERNAL | USER)
 * @returns {string}
 */
export const getDefaultRouteByRole = (role) => {
  const normalizedRole = role?.toString().toUpperCase();

  if (normalizedRole === 'ADMIN') {
    return PROTECTED_ROUTES.ADMIN_DASHBOARD;
  }

  if (normalizedRole === 'INTERNAL' || normalizedRole === 'USER') {
    return PROTECTED_ROUTES.DASHBOARD;
  }

  return AUTH_ROUTES.LOGIN;
};

/**
 * Update document title based on route meta
 * @param {Object} route - Route configuration
 */
export const updateDocumentTitle = (route) => {
  if (route.meta?.title) {
    document.title = route.meta.title;
  }
};

/**
 * Update document meta description
 * @param {Object} route - Route configuration
 */
export const updateMetaDescription = (route) => {
  if (route.meta?.description) {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', route.meta.description);
    }
  }
};

/**
 * Find route by path
 * @param {Array} routes - Route array
 * @param {string} path - Path to find
 * @returns {Object|null}
 */
export const findRouteByPath = (routes, path) => {
  return routes.find((route) => route.path === path) || null;
};

/**
 * Get all route paths
 * @param {Array} routes - Route array
 * @returns {Array}
 */
export const getAllRoutePaths = (routes) => {
  return routes.map((route) => route.path);
};

/**
 * Validate route configuration
 * @param {Object} route - Route configuration
 * @returns {boolean}
 */
export const isValidRoute = (route) => {
  return route.path && route.component && route.name;
};

export default {
  generateBreadcrumbs,
  requiresAuth,
  requiresGuest,
  hasRequiredRole,
  updateDocumentTitle,
  updateMetaDescription,
  findRouteByPath,
  getAllRoutePaths,
  isValidRoute,
  getDefaultRouteByRole,
};
